import { useState } from 'react';
import { useAlertDialog } from '../../hooks/useAlertDialog';
import AlertDialog from '../AlertDialog';
import { Upload, X } from 'lucide-react';

interface HomepageImageFormProps {
  onAddImage: (file: File, imageData: any) => Promise<void>;
}

export const HomepageImageForm: React.FC<HomepageImageFormProps> = ({ onAddImage }) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [topPos, setTopPos] = useState('10%');
  const [leftPos, setLeftPos] = useState('5%');
  const [rotation, setRotation] = useState('-5deg');
  const [width, setWidth] = useState('50vw');
  const [height, setHeight] = useState('auto');
  const [zIndex, setZIndex] = useState(1);
  const { isOpen, title, message, setAlert, clearAlert } = useAlertDialog();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!imageFile) {
      setAlert('Missing Image', 'Please select an image');
      return;
    }

    try {
      await onAddImage(imageFile, {
        top_pos: topPos,
        left_pos: leftPos,
        rotation,
        width_val: width,
        height_val: height,
        z_index_val: zIndex
      });

      // Reset form
      setImageFile(null);
      setPreview(null);
      setTopPos('10%');
      setLeftPos('5%');
      setRotation('-5deg');
      setWidth('50vw');
      setHeight('auto');
      setZIndex(1);

      setAlert('Success', 'Image added successfully');
    } catch (error) {
      setAlert('Error', 'Failed to add image');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-2xl font-semibold mb-6">Add Homepage Image</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="border-2 border-dashed rounded-lg p-4">
          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-48 object-contain rounded-lg"
              />
              <button
                type="button"
                onClick={() => {
                  setImageFile(null);
                  setPreview(null);
                }}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
              <Upload size={32} className="text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Click to select an image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="topPos" className="block text-sm font-medium text-gray-700">
              Top Position
            </label>
            <input
              type="text"
              id="topPos"
              value={topPos}
              onChange={(e) => setTopPos(e.target.value)}
              placeholder="e.g., 10% or 50vh"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="leftPos" className="block text-sm font-medium text-gray-700">
              Left Position
            </label>
            <input
              type="text"
              id="leftPos"
              value={leftPos}
              onChange={(e) => setLeftPos(e.target.value)}
              placeholder="e.g., 10% or 50vw"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="rotation" className="block text-sm font-medium text-gray-700">
              Rotation
            </label>
            <input
              type="text"
              id="rotation"
              value={rotation}
              onChange={(e) => setRotation(e.target.value)}
              placeholder="e.g., -5deg"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="width" className="block text-sm font-medium text-gray-700">
              Width
            </label>
            <input
              type="text"
              id="width"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="e.g., 50vw"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="height" className="block text-sm font-medium text-gray-700">
              Height
            </label>
            <input
              type="text"
              id="height"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="e.g., auto"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="zIndex" className="block text-sm font-medium text-gray-700">
            Z-Index
          </label>
          <input
            type="number"
            id="zIndex"
            value={zIndex}
            onChange={(e) => setZIndex(parseInt(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <button
          type="submit"
          disabled={!imageFile}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Add Image
        </button>
      </form>

      <AlertDialog
        isOpen={isOpen}
        title={title}
        message={message}
        onClose={clearAlert}
      />
    </div>
  );
};